import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { auth, ApiError, extRedirectOk } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ChangeEmail } from '../components/ChangeEmail';
import logoBar from '../assets/logo-bar.png';
import heroImg from '../assets/login-hero.webp';
import './login.css';

/**
 * Sign in / sign up. Two methods land on the same account:
 *   - Continue with Google (unchanged — the original and still the default CTA)
 *   - Email + password
 * One account per email address, so a person who signed up one way and comes
 * back the other reaches the same sections, quota and plan (the backend links
 * them by email; see the Google callback in api/index.php).
 *
 * Deliberately NOT gated on email verification: a new account can sign up and
 * use the free tier straight away, and the confirm link is only enforced when
 * they try to upgrade. Putting a mail round-trip in front of someone's first
 * separation is the surest way to lose them before they've seen it work.
 *
 * The presentation is the Claude Design "Riffloop Login" split screen — a hero
 * panel carrying the pitch on the left, the auth card on the right — and it is
 * DARK IN BOTH THEMES on purpose (see login.css). This page renders without the
 * TopBar (App.tsx), so the hero's own logo is the only lockup on screen.
 */
/**
 * Email+password sign-in is BUILT, DEPLOYED and working — but hidden from this
 * page (owner's call, 2026-08-15) until they've tested it themselves. Flip this
 * to `true` to bring back the email/password fields, the submit button and the
 * create-account / forgot-password links. Nothing else needs to change.
 *
 * Scope of the flag: THIS PAGE ONLY. The /api/auth/* endpoints stay live so the
 * flow can be exercised (that's the point), and /reset-password still works for
 * anyone holding a link. Google sign-in is deliberately outside the flag and
 * behaves exactly as it always has.
 */
const EMAIL_AUTH_ENABLED = true;

type Mode = 'signup' | 'login' | 'forgot';

// The pitch chips under the hero copy — the four things people actually come
// here to do, in the stem colours the mixer uses for the same parts.
const CHIPS: { label: string; dot: string; border: string; bg: string }[] = [
  { label: 'Karaoke', dot: '#a78bfa', border: 'rgba(167,139,250,.38)', bg: 'rgba(167,139,250,.12)' },
  { label: 'Backing track', dot: '#fb923c', border: 'rgba(251,146,60,.38)', bg: 'rgba(251,146,60,.12)' },
  { label: 'Practice a part', dot: '#38bdf8', border: 'rgba(56,189,248,.38)', bg: 'rgba(56,189,248,.12)' },
  { label: 'Full mixer', dot: '#34d399', border: 'rgba(52,211,153,.38)', bg: 'rgba(52,211,153,.12)' },
];

export function Login() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  // Resume intent (?next=…) — e.g. a /checkout deep-link that bounced anonymous
  // users here. Restricted to same-origin paths so it can't become an open
  // redirect. Falls back to Protected's in-memory router state.from.
  const rawNext = params.get('next');
  const next =
    rawNext && rawNext.startsWith('/') && !rawNext.startsWith('//') ? rawNext : undefined;

  // Extension sign-in handoff. /extension-auth sends us here when the popup has
  // no session yet, so the user can pick EITHER method; this carries the
  // extension's own redirect through to the Google flow so an OAuth failure can
  // still bounce back and close the popup. The email+password path doesn't need
  // it — it returns via ?next=, which already points at /extension-auth.
  const rawExt = params.get('ext_redirect');
  const extRedirect = rawExt && extRedirectOk(rawExt) ? rawExt : undefined;

  // ?verified=1 / ?verify_error=1 come back from /api/auth/verify when the
  // confirm link was followed in a browser with no session.
  const verified = params.get('verified') === '1';
  const verifyError = params.get('verify_error') === '1';
  // An Apple sign-in that did not finish comes back as ?err=apple… (a cancel at Apple needs no note).
  const appleErr = (params.get('err') || '').startsWith('apple') && params.get('err') !== 'apple_cancelled';

  // Sign in with Apple is offered only when the backend says it is switched on. Anything else —
  // switched off, an older backend without the route, a network error — simply shows no button.
  const [apple, setApple] = useState(false);
  useEffect(() => {
    let live = true;
    auth
      .providers()
      .then((p) => {
        if (live) setApple(p?.apple === true);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  // The email path always starts at LOG IN. Returning users outnumber new ones
  // on a sign-in page, and someone who needs an account reaches signup from the
  // "New to Riffloop?" link below the form — the same order Moises uses.
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [sent, setSent] = useState(false); // forgot-password confirmation
  // Holds the address of an account waiting to be activated — set after signup,
  // and after a login attempt on an account that hasn't activated yet. The card
  // switches to a blocking "check your inbox" state: under activation-first
  // there is no session to continue into, so there is nothing to skip past.
  // The address is printed back because this is the ONE moment a typo can be
  // caught, and "Fix it" is the only thing standing between a mistyped address
  // and a dead account.
  const [pending, setPending] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!loading && user && !pending) {
      // Already signed in (session existed): honor ?next=, else where Protected
      // bounced us from (preserves ?handoff=…), else home. `pending` holds the
      // redirect so the activation wall isn't skipped past.
      const from = (location.state as { from?: string } | null)?.from;
      navigate(next || from || '/', { replace: true });
    }
  }, [loading, user, navigate, location.state, next, pending]);

  function switchMode(m: Mode) {
    setMode(m);
    setErr(null);
    setSent(false);
    // Keep whatever they've already typed — retyping an email to switch tabs is
    // a small thing that reads as the form not paying attention.
    setTimeout(() => emailRef.current?.focus(), 0);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setErr(null);
    setBusy(true);
    try {
      if (mode === 'forgot') {
        await auth.forgotPassword(email.trim());
        // Shown whatever the outcome — the backend answers identically for a
        // known and an unknown address, and so must we.
        setSent(true);
        return;
      }
      if (mode === 'signup') {
        const res = await auth.signup(email.trim(), password);
        // No session is created — show the activation wall and stop. Do NOT
        // refresh(): there is nothing to refresh into.
        setPending(res.email || email.trim());
        return;
      }
      await auth.login(email.trim(), password);
      // Session cookie is set; refresh() populates the context and the effect
      // above performs the redirect (honouring ?next=).
      await refresh();
    } catch (e2) {
      const api = e2 as ApiError & { email?: string };
      if (api?.code === 'not_activated') {
        // They have an account but never followed the link — same wall as after
        // signup, with the same resend and fix-address controls.
        setPending(email.trim());
      } else if (api?.code === 'email_taken_google') {
        // Their account predates password sign-in entirely — the only control
        // that works for them is the Google button, so point straight at it.
        setErr('That email is signed up with Google — use the "Continue with Google" button above.');
        setMode('login');
      } else if (api?.code === 'email_taken') {
        // Steer rather than scold: the account exists, they just need the other tab.
        setErr('That email already has an account. Log in instead, or reset the password.');
        setMode('login');
      } else {
        setErr(api?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  const submitLabel =
    mode === 'signup' ? 'Sign up for free' : mode === 'login' ? 'Log in' : 'Send reset link';

  const heading = pending
    ? { title: 'Check your inbox', sub: 'One click on that link and your account is live.' }
    : mode === 'signup'
      ? { title: 'Create your free account', sub: 'Free to start — no card needed.' }
      : mode === 'forgot'
        ? {
            title: 'Reset your password',
            sub: "Enter your email and we'll send you a link to set a new password.",
          }
        : { title: 'Welcome back', sub: 'Log in to pick up your last session.' };

  return (
    <div className="lg-page">
      <section className="lg-hero">
        {/* Decorative: the pitch is in the text beside it, so it carries no alt. */}
        <img className="lg-hero-img" src={heroImg} alt="" aria-hidden="true" />
        <div className="lg-hero-scrim" />
        <div className="lg-hero-scrim-b" />

        <a href="https://riffloop.app/" aria-label="Riffloop home" style={{ position: 'relative' }}>
          <img className="lg-hero-logo" src={logoBar} alt="Riffloop" />
        </a>

        <div className="lg-hero-body">
          <div className="lg-eyebrow">
            <span>Six stems · one track</span>
          </div>
          <h1>
            Practice any song.
            <br />
            Hear every part.
          </h1>
          <p className="lg-hero-lede">
            Drop in a track and Riffloop splits it into six parts. Loop the hard bar, mute the
            guitar, sing over the rest.
          </p>
          <div className="lg-chips">
            {CHIPS.map((c) => (
              <span
                key={c.label}
                className="lg-chip"
                style={
                  {
                    '--chip-dot': c.dot,
                    '--chip-border': c.border,
                    '--chip-bg': c.bg,
                  } as React.CSSProperties
                }
              >
                {c.label}
              </span>
            ))}
          </div>
        </div>

        <p className="lg-hero-legal">
          By using Riffloop you confirm you have the rights to the audio you process.{' '}
          <a href="/tos">See Terms</a>.
        </p>
      </section>

      <section className="lg-panel">
        <div className="lg-card">
          <div className="lg-head">
            <h2>{heading.title}</h2>
            <p>{heading.sub}</p>
          </div>

          {verified && <LgBanner tone="ok">Email confirmed — you're all set.</LgBanner>}
          {appleErr && (
            <LgBanner tone="warn">Sign in with Apple didn't finish. Please try again.</LgBanner>
          )}
          {verifyError && (
            <LgBanner tone="warn">
              That confirmation link has expired or was already used. Log in and we'll send a new
              one.
            </LgBanner>
          )}

          {pending ? (
            <>
              <LgBanner tone="ok">
                Account created. Click the link we sent to <strong>{pending}</strong> to activate it.
              </LgBanner>
              <p className="lg-pending-note">
                Check your spam folder if it hasn't arrived in a minute.
              </p>
              {/* The only two things a person stuck here can act on. Both work
                  without a session — the backend keeps a pending marker for this
                  browser precisely so they do. */}
              <div className="lg-pending-actions">
                <ResendActivation />
                <ChangeEmail onChanged={(e) => setPending(e)} />
              </div>
            </>
          ) : (
            <>
              {mode !== 'forgot' && (
                <>
                  <div className="lg-providers">
                    <button
                      type="button"
                      className="lg-google"
                      onClick={() => auth.startGoogle(next, extRedirect)}
                    >
                      <GoogleG /> Continue with Google
                    </button>
                    {apple && (
                      <button
                        type="button"
                        className="lg-apple"
                        onClick={() => auth.startApple(next, extRedirect)}
                      >
                        <AppleLogo /> Continue with Apple
                      </button>
                    )}
                  </div>
                  {EMAIL_AUTH_ENABLED && <div className="lg-or">OR</div>}
                </>
              )}

              {!EMAIL_AUTH_ENABLED ? null : sent ? (
                <LgBanner tone="ok">
                  If that email has a Riffloop account, a reset link is on its way. It works for one
                  hour.
                </LgBanner>
              ) : (
                <form onSubmit={onSubmit} className="lg-fields">
                  <label className="lg-field">
                    <span>Email</span>
                    <input
                      ref={emailRef}
                      className="lg-input"
                      type="email"
                      name="email"
                      autoComplete="email"
                      placeholder="you@band.com"
                      value={email}
                      onChange={(ev) => setEmail(ev.target.value)}
                      required
                      disabled={busy}
                    />
                  </label>

                  {mode !== 'forgot' && (
                    <label className="lg-field">
                      <span className="lg-field-row">
                        Password
                        {mode === 'login' && (
                          <button
                            type="button"
                            className="lg-linkbtn lg-linkbtn--quiet"
                            onClick={() => switchMode('forgot')}
                          >
                            Forgot?
                          </button>
                        )}
                      </span>
                      <span className="lg-pw-wrap">
                        <input
                          className="lg-input"
                          type={showPw ? 'text' : 'password'}
                          name="password"
                          // Tells a password manager to offer to save vs to fill.
                          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                          placeholder={mode === 'signup' ? 'At least 8 characters' : '••••••••'}
                          value={password}
                          onChange={(ev) => setPassword(ev.target.value)}
                          required
                          minLength={8}
                          maxLength={72}
                          disabled={busy}
                        />
                        <button
                          type="button"
                          className="lg-pw-toggle"
                          onClick={() => setShowPw((v) => !v)}
                          aria-label={showPw ? 'Hide password' : 'Show password'}
                        >
                          {showPw ? 'Hide' : 'Show'}
                        </button>
                      </span>
                    </label>
                  )}

                  {err && <LgBanner tone="err">{err}</LgBanner>}

                  <button type="submit" className="lg-submit" disabled={busy}>
                    {busy ? 'One moment…' : submitLabel}
                  </button>
                </form>
              )}

              {EMAIL_AUTH_ENABLED && (
                <p className="lg-alt">
                  {mode === 'login' && (
                    <>
                      New to Riffloop?{' '}
                      <button className="lg-linkbtn" onClick={() => switchMode('signup')}>
                        Create an account
                      </button>
                    </>
                  )}
                  {mode === 'signup' && (
                    <>
                      Already have an account?{' '}
                      <button className="lg-linkbtn" onClick={() => switchMode('login')}>
                        Sign in
                      </button>
                    </>
                  )}
                  {mode === 'forgot' && (
                    <button className="lg-linkbtn" onClick={() => switchMode('login')}>
                      Back to sign in
                    </button>
                  )}
                </p>
              )}
            </>
          )}

          <p className="lg-legal">
            By continuing you agree to our <a href="/tos">Terms</a> and{' '}
            <a href="/privacy">Privacy Policy</a>.
          </p>
        </div>
      </section>
    </div>
  );
}

// Resend the activation link. Lives here rather than in VerifyEmailNotice
// because that one is the upgrade-time gate and assumes a signed-in user; this
// one runs with no session at all.
function ResendActivation() {
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  if (state === 'sent') {
    return <span style={{ fontSize: 12.5, color: '#4ade80' }}>Sent — check your inbox again.</span>;
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <button
        type="button"
        className="lg-linkbtn"
        style={{ fontSize: 13 }}
        disabled={state === 'sending'}
        onClick={async () => {
          if (state === 'sending') return;
          setState('sending');
          try {
            await auth.sendVerification();
            setState('sent');
          } catch {
            // Includes the per-account rate limit — don't dress a refusal up as a send.
            setState('failed');
          }
        }}
      >
        {state === 'sending' ? 'Sending…' : 'Resend the link'}
      </button>
      {state === 'failed' && (
        <span style={{ fontSize: 12, color: '#767d8f' }}>Couldn't send — try again shortly.</span>
      )}
    </span>
  );
}

// Login-page banner, in this page's own dark palette. The exported <Banner>
// below stays on the app tokens because /reset-password renders it inside the
// normal themed chrome.
function LgBanner({ tone, children }: { tone: 'ok' | 'warn' | 'err'; children: React.ReactNode }) {
  return (
    <div role={tone === 'err' ? 'alert' : 'status'} className={`lg-banner lg-banner--${tone}`}>
      <span>{children}</span>
    </div>
  );
}

export function Banner({ tone, children }: { tone: 'ok' | 'warn' | 'err'; children: React.ReactNode }) {
  const color =
    tone === 'ok'
      ? 'var(--success-green)'
      : tone === 'err'
        ? 'var(--mute-red)'
        : 'var(--warning-amber)';
  return (
    <div
      role={tone === 'err' ? 'alert' : 'status'}
      style={{
        border: `1px solid ${color}`,
        background: 'var(--panel-2)',
        color: 'var(--text)',
        borderRadius: 8,
        padding: '9px 12px',
        fontSize: 12.5,
        lineHeight: 1.5,
        textAlign: 'left',
        margin: '10px 0 0',
      }}
    >
      {children}
    </div>
  );
}

// Apple's logo, in white on the black button, as Apple's button guidelines ask.
function AppleLogo() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.6 9.2c0-.6-.05-1.18-.15-1.73H9v3.28h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.56 2.66-3.87 2.66-6.53Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.25c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.58-5.05-3.71H.94v2.33A9 9 0 0 0 9 18Z"
      />
      <path fill="#FBBC05" d="M3.95 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.94a9 9 0 0 0 0 8.1l3.01-2.33Z" />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .94 4.95l3.01 2.33C4.66 5.15 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}
